import { useCallback, useMemo, useState, useEffect } from "react";
import React from "react";
import { syncVisitorId } from "@/utils/visitorIdSync";
import { mutate } from "swr";
import {
  Cog6ToothIcon,
  CreditCardIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import { isAdmin } from "@/components/context/useTeamMembers";
import usePaymentMethods from "@/components/context/usePaymentMethods";
import CenteredModalDialog from "@/components/blocks/CenteredModalDialog";
import Link from "next/link";
import { trackPurchase } from "@/utils/analytics";
import {
  type Subscription,
  allSubscriptions,
  subscriptionPlan,
  planName,
  getSubscriptionState,
} from "@/utils/billing";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/Alert";
import { PageHeader } from "@/components/ui/page-header";
import CompanyInfoCard from "@/components/billing/CompanyInfoCard";
import RecentInvoicesCard from "@/components/billing/RecentInvoicesCard";

const emailPackages = [
  { email: 1000, price: 495 },
  { email: 3000, price: 995 },
  { email: 10000, price: 1995 },
];

// The endpoint answers with a stable reason code, never raw upstream text.
// card_declined covers every non-entitled subscription Stripe returns, including the
// `incomplete` a 3DS/SCA card produces. Telling that customer to add a new card sends
// them round the support loop in the 2026-04-16 3DS design doc: a fresh card does
// nothing for a PaymentIntent stuck in requires_action, which only the confirmation
// link in their email resolves.
const reactivateErrorMessages: Record<string, string> = {
  card_declined:
    "The payment did not complete, so the subscription did not start and the workspace is still suspended. If your bank asked you to confirm the payment, check your email for the confirmation link; otherwise update your payment method below and try again.",
  billing_provider_error:
    "We could not reach our billing provider. Nothing was charged — please try again in a few minutes.",
  not_suspended: "This workspace is already active.",
  not_stripe_account: "This workspace is not billed through Stripe. Please contact support.",
};

const planChangeErrorMessages: Record<string, string> = {
  suspended: "Your workspace is suspended. Use the Reactivate button at the top of this page.",
  stripe_error: "We could not update your subscription. Please try again in a few minutes.",
  billing_provider_error:
    "We could not reach our billing provider. Please try again in a few minutes.",
};

export default function DashboardSettingsPage() {
  const { account, team } = useCurrentAccount({ requireLoggedIn: true });
  const {
    paymentMethods,
    isLoading: paymentMethodsLoading,
    isError: paymentMethodsError,
  } = usePaymentMethods();
  const [changePlanFormOpen, setChangePlanFormOpen] = useState(false);
  const [changePlanProcessing, setChangePlanProcessing] = useState(false);
  const [paymentMethodDeleting, setPaymentMethodDeleting] = useState(false);
  const [makingDefault, setMakingDefault] = useState(false);

  const [prepaidEmailFormOpen, setPrepaidEmailFormOpen] = useState(false);
  const [prepaidEmailProcessing, setPrepaidEmailProcessing] = useState(false);

  const [reactivateProcessing, setReactivateProcessing] = useState(false);
  const [reactivateError, setReactivateError] = useState<string | null>(null);

  useEffect(() => {
    // Sync visitor ID when page loads
    syncVisitorId();
  }, []);

  const currentSubscription = useMemo(
    () => subscriptionPlan(account?.current_plan || ""),
    [account?.current_plan],
  );

  const subscriptionState = useMemo(() => getSubscriptionState(account), [account]);

  const addPaymentMethodClicked = useCallback(() => {
    if (changePlanProcessing) return;
    setChangePlanProcessing(true);

    fetch(`/web_api/billing/payment_methods`, {
      method: "POST",
    })
      .then((resp) => {
        if (resp.status === 200) {
          resp
            .json()
            .then((json) => {
              if (json.status === "ok") {
                if (json.url) {
                  window.location.href = json.url;
                }
              } else {
                alert(json.error || "Error. Try again later.");
              }

              setChangePlanProcessing(false);
            })
            .catch((e) => {
              alert(e.toString() || "Error. Try again later.");
              setChangePlanProcessing(false);
            });
        } else {
          alert("We could not start the card setup. Please try again in a few minutes.");
          setChangePlanProcessing(false);
        }
      })
      .catch((e) => {
        alert(e.toString() || "Error. Try again later.");
        setChangePlanProcessing(false);
      });
  }, [changePlanProcessing]);

  const reactivateClicked = useCallback(() => {
    if (reactivateProcessing) return;

    setReactivateProcessing(true);
    setReactivateError(null);

    fetch(`/web_api/billing/reactivate`, {
      method: "POST",
    })
      .then((resp) => {
        if (resp.status === 200) {
          mutate("/web_api/accounts/current");
          return;
        }

        return resp
          .json()
          .then((json) => {
            if (resp.status === 402 && json.reason === "no_payment_method") {
              addPaymentMethodClicked();
              return;
            }

            setReactivateError(reactivateErrorMessages[json.reason] || "Error. Try again later.");
          })
          .catch(() => {
            setReactivateError("Error. Try again later.");
          });
      })
      .catch((e) => {
        setReactivateError(e.toString() || "Error. Try again later.");
      })
      .finally(() => {
        setReactivateProcessing(false);
      });
  }, [reactivateProcessing, addPaymentMethodClicked]);

  const changePlanBuyButtonClicked = useCallback(
    (planName: string, subscriptionTerm?: "monthly" | "annually") => {
      if (changePlanProcessing) return;

      setChangePlanProcessing(true);

      const planId = `${planName}-${subscriptionTerm}`;

      fetch(`/web_api/billing/change_subscription/${planId}`, {
        method: "POST",
      }).then((resp) => {
        if (resp.status === 200) {
          resp
            .json()
            .then((json) => {
              if (json.status === "subscription_changed") {
                const subscription = subscriptionPlan(planName);
                if (subscription && subscription.price > 0) {
                  const price = subscription.price / 100;
                  trackPurchase(price, "USD", subscription.name, subscriptionTerm || "monthly");
                }

                mutate("/web_api/accounts/current");
                setChangePlanFormOpen(false);
              } else if (json.status === "already_on_this_plan") {
                alert("You are already on this plan.");
                setChangePlanFormOpen(false);
              } else if (json.requires_payment_method) {
                alert(
                  "A payment method is required for this plan. Please add a payment method first.",
                );
                setChangePlanFormOpen(false);
                // Automatically open the payment method form
                setTimeout(() => {
                  addPaymentMethodClicked();
                }, 100);
              } else {
                alert(json.error || json.reason || "Error. Try again later.");
              }

              setChangePlanProcessing(false);
            })
            .catch((e) => {
              alert(e.toString() || "Error. Try again later.");
              setChangePlanProcessing(false);
            });
        } else {
          resp
            .json()
            .then((json) => {
              if (json.requires_payment_method) {
                alert(
                  "A payment method is required to restart your subscription. Please add a card first.",
                );
                setChangePlanFormOpen(false);
                setChangePlanProcessing(false);
                setTimeout(() => {
                  addPaymentMethodClicked();
                }, 100);
                return;
              }
              alert(
                json.error || planChangeErrorMessages[json.reason] || "Error. Try again later.",
              );
              setChangePlanProcessing(false);
            })
            .catch(() => {
              alert("Error. Try again later.");
              setChangePlanProcessing(false);
            });
        }
      });
    },
    [changePlanProcessing, addPaymentMethodClicked],
  );

  const deletePaymentMethodClicked = useCallback(
    (paymentMethodId: string) => {
      if (paymentMethodDeleting) return;

      setPaymentMethodDeleting(true);

      fetch(`/web_api/billing/payment_methods/${paymentMethodId}`, {
        method: "DELETE",
      }).then((resp) => {
        if (resp.status === 200) {
          mutate("/web_api/billing/payment_methods");
          mutate("/web_api/accounts/current");
          setPaymentMethodDeleting(false);
        } else {
          resp
            .json()
            .then((json) => {
              if (json.error) {
                alert("Error: " + json.error);
              } else {
                alert("Error. Try again later.");
              }
              setPaymentMethodDeleting(false);
            })
            .catch((_e) => {
              alert("Error. Try again later.");
              setPaymentMethodDeleting(false);
            });
        }
      });
    },
    [paymentMethodDeleting],
  );

  const makeDefaultPaymentMethodClicked = useCallback(
    (paymentMethodId: string) => {
      if (makingDefault) return;
      setMakingDefault(true);

      fetch(`/web_api/billing/payment_methods/${paymentMethodId}/make_default`, {
        method: "POST",
      })
        .then((resp) => {
          if (resp.status === 200) {
            mutate("/web_api/accounts/current");
            mutate("/web_api/billing/payment_methods");
          } else {
            resp
              .json()
              .then((json) => {
                alert(json.error || json.reason || "Error. Try again later.");
              })
              .catch(() => {
                alert("Error. Try again later.");
              });
          }
        })
        .finally(() => {
          setMakingDefault(false);
        });
    },
    [makingDefault],
  );

  const prepaidEmailBuyButtonClicked = useCallback(
    (emailCount: number) => {
      if (prepaidEmailProcessing) return;

      setPrepaidEmailProcessing(true);

      fetch(`/web_api/billing/create_checkout_session/email_${emailCount}`, {
        method: "POST",
      })
        .then((resp) => resp.json())
        .then((json) => {
          if (json.status === "ok") {
            if (json.url) {
              window.location.href = json.url;
            }
          } else {
            alert(json.error || "Error. Try again later.");
          }
        })
        .catch((e) => {
          alert(e.toString() || "Error. Try again later.");
        })
        .finally(() => {
          setPrepaidEmailProcessing(false);
        });
    },
    [prepaidEmailProcessing],
  );

  const subscriptionStatus = useMemo(() => {
    if (!account?.subscription) return <span>&nbsp;</span>;

    if (account.subscription.status === "trialing") {
      const trialEnd = new Date(account.subscription.trial_end! * 1000);
      return `Trial ends on ${trialEnd.toLocaleDateString()}`;
    }

    if (account.subscription.status === "active") {
      const currentPeriodEnd = new Date(account.subscription.current_period_end! * 1000);
      return (
        <span>
          Next ${account.subscription.amount / 100} payment on{" "}
          {currentPeriodEnd.toLocaleDateString()}
        </span>
      );
    }

    if (account.subscription.status === "past_due") {
      return <span>Past due</span>;
    }

    if (account.subscription.status === "unpaid") {
      return <span>Unpaid</span>;
    }

    return account.subscription.status;
  }, [account]);

  return (
    <DashboardLayout
      activePath="/dashboard/billing"
      title="Billing"
      breadcrumbs={[{ label: "Billing" }]}
    >
      <div className="mx-auto max-w-6xl px-4 py-8">
        <PageHeader title="Billing" description="Manage your subscription and payment methods" />

        {team && !isAdmin(team.role) ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center">
                <h3 className="text-lg font-medium mb-2">Access Restricted</h3>
                <p className="text-muted-foreground">
                  Only account administrators can access billing settings. Contact your team admin
                  if you need to make changes.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {subscriptionState.status === "suspended" && (
              <Alert variant="destructive" data-testid="suspended-banner">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertTitle>Workspace suspended</AlertTitle>
                <AlertDescription>
                  <p className="mb-3">
                    Alerts are not being sent, and your public status pages show a billing notice.
                    Your monitors are still running and no data has been deleted.
                  </p>
                  {reactivateError && (
                    <p className="mb-3 text-sm text-destructive">{reactivateError}</p>
                  )}
                  <Button size="sm" onClick={reactivateClicked} disabled={reactivateProcessing}>
                    {reactivateProcessing ? "Reactivating..." : "Reactivate"}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {subscriptionState.status === "trialing" && (
              <Alert data-testid="trial-active-banner">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertTitle>Free trial active</AlertTitle>
                <AlertDescription>
                  Your free trial ends in {subscriptionState.daysRemaining} day
                  {subscriptionState.daysRemaining !== 1 ? "s" : ""} (
                  {subscriptionState.trialEnd.toLocaleDateString()}). Add a payment method to
                  continue after the trial.
                </AlertDescription>
              </Alert>
            )}

            {/* Current Plan Section */}
            <Card>
              <CardHeader>
                <CardTitle>Subscription</CardTitle>
                <CardDescription>Your current plan and usage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">Current Plan</p>
                    <p className="text-2xl font-bold" data-testid="current-plan-name">
                      {planName(account?.current_plan || "")}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      Daily free emails limit: {currentSubscription.daily_emails}
                    </p>
                    {subscriptionStatus && (
                      <p className="text-sm text-muted-foreground">{subscriptionStatus}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => setChangePlanFormOpen(true)}
                    disabled={changePlanProcessing}
                  >
                    Change plan
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Company Information Section */}
            <CompanyInfoCard />

            {/* Recent Invoices Section */}
            <RecentInvoicesCard />

            {/* Payment Methods Section */}
            <Card>
              <CardHeader>
                <CardTitle>Payment Methods</CardTitle>
                <CardDescription>Manage your payment methods</CardDescription>
              </CardHeader>
              <CardContent>
                {paymentMethodsLoading ? (
                  <div className="text-muted-foreground">Loading payment methods...</div>
                ) : paymentMethodsError ? (
                  <div className="text-destructive">Cannot load payment methods.</div>
                ) : paymentMethods.length === 0 ? (
                  <div className="text-muted-foreground mb-4">No payment methods added yet.</div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {paymentMethods.map((paymentMethod) => (
                      <div
                        key={paymentMethod._id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                        data-testid="payment-method-card"
                        data-payment-method-id={paymentMethod._id}
                        data-card-type={`${paymentMethod.card_brand.toLowerCase()}-${paymentMethod.last4}`}
                        data-is-default={account?.current_payment_method === paymentMethod._id}
                      >
                        <div className="flex items-center gap-3">
                          <CreditCardIcon className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {paymentMethod.card_brand} ending in {paymentMethod.last4}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {account?.current_payment_method === paymentMethod._id ? (
                            <Badge variant="secondary">Default</Badge>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => makeDefaultPaymentMethodClicked(paymentMethod._id)}
                                disabled={makingDefault}
                              >
                                Make Default
                                {makingDefault && (
                                  <Cog6ToothIcon className="ml-2 animate-spin h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => deletePaymentMethodClicked(paymentMethod._id)}
                                disabled={paymentMethodDeleting}
                              >
                                Delete
                                {paymentMethodDeleting && (
                                  <Cog6ToothIcon className="ml-2 animate-spin h-4 w-4" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={addPaymentMethodClicked} disabled={changePlanProcessing}>
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Add payment method
                </Button>
              </CardContent>
            </Card>

            {/* Prepaid Credits Section */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Email Credits */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Email Credits</CardTitle>
                  </div>
                  <CardDescription>Additional email notifications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-3xl font-bold">{account?.prepaid_email_messages || 0}</p>
                      <p className="text-sm text-muted-foreground">messages remaining</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setPrepaidEmailFormOpen(true)}
                      data-testid="buy-email-button"
                    >
                      Buy email package
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* SMS Credits (Deprecated) - only show when user has remaining credits */}
              {account && account.prepaid_sms_messages > 0 && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <ChatBubbleLeftIcon className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>SMS Credits (Deprecated)</CardTitle>
                    </div>
                    <CardDescription>
                      SMS notifications are no longer available for new contacts
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-3xl font-bold">{account.prepaid_sms_messages}</p>
                        <p className="text-sm text-muted-foreground">messages remaining</p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Remaining credits will continue to work for existing SMS contacts.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      <CenteredModalDialog
        show={changePlanFormOpen}
        onClose={() => setChangePlanFormOpen(false)}
        className="!max-w-3xl"
      >
        <div className="bg-card shadow-xl rounded-lg w-full">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-2xl font-semibold text-foreground">Change Subscription Plan</h3>
          </div>

          <div className="p-6">
            <h4 className="text-xl font-medium text-foreground mb-6 text-center">
              Uptime Monitor Pro
            </h4>

            <div className="space-y-4">
              {allSubscriptions.map((subscription) => (
                <div
                  key={subscription.id}
                  className="bg-card rounded-lg p-4 border border-border"
                  data-testid={`subscription-plan-${subscription.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-lg font-medium text-foreground">{subscription.name}</h5>
                      <p className="text-sm text-muted-foreground">
                        {subscription.monitors} monitors • {subscription.daily_emails} daily emails
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => changePlanBuyButtonClicked(subscription.id, "monthly")}
                        disabled={changePlanProcessing}
                        data-testid={`subscribe-monthly-${subscription.id}`}
                      >
                        {`$${subscription.price / 100}/month`}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CenteredModalDialog>

      <CenteredModalDialog
        show={prepaidEmailFormOpen}
        onClose={() => setPrepaidEmailFormOpen(false)}
        className="!max-w-lg"
      >
        <div className="bg-card shadow-xl rounded-lg w-full">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-2xl font-semibold text-foreground flex items-center">
              <EnvelopeIcon className="h-6 w-6 mr-2 text-blue-500" />
              Buy Email Package
            </h3>
          </div>

          <div className="p-6">
            <p className="text-muted-foreground mb-6 text-center">
              Purchase email credits for additional notifications beyond your plan&apos;s daily
              limit
            </p>
            <div className="space-y-4">
              {emailPackages.map((emailPackage) => (
                <div
                  key={`email-${emailPackage.email}`}
                  className="bg-card rounded-lg p-4 border border-border hover:border-muted-foreground transition-colors"
                  data-testid={`email-package-${emailPackage.email}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-lg font-medium text-foreground">
                        {emailPackage.email.toLocaleString()} emails
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        ${((emailPackage.price / 100 / emailPackage.email) * 1000).toFixed(2)} per
                        1,000 emails
                      </p>
                    </div>
                    <Button
                      onClick={() => prepaidEmailBuyButtonClicked(emailPackage.email)}
                      disabled={prepaidEmailProcessing}
                      data-testid={`buy-email-package-${emailPackage.email}`}
                    >
                      ${emailPackage.price / 100}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CenteredModalDialog>
    </DashboardLayout>
  );
}
